<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { systemApi } from '@/api/systemApi'
import { toApiError } from '@/api/httpClient'

type State = 'checking' | 'online' | 'offline'

const state = ref<State>('checking')
const detail = ref<string>('Checking backend…')

async function check(): Promise<void> {
  state.value = 'checking'
  detail.value = 'Checking backend…'
  try {
    const report = await systemApi.ready()
    state.value = report.status === 'Healthy' ? 'online' : 'offline'
    detail.value =
      report.status === 'Healthy'
        ? 'Backend ready'
        : `Backend degraded (${report.status})`
  } catch (err) {
    state.value = 'offline'
    detail.value = toApiError(err).title
  }
}

onMounted(check)
</script>

<template>
  <button class="status" :data-state="state" @click="check" :title="detail">
    <span class="dot" />
    <span class="label">{{ detail }}</span>
  </button>
</template>

<style scoped>
.status {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: transparent;
  border: 1px solid var(--border);
  border-radius: 999px;
  padding: 0.35rem 0.7rem;
  color: var(--text-muted);
  font-size: 0.8rem;
}

.dot {
  width: 0.6rem;
  height: 0.6rem;
  border-radius: 50%;
  background: var(--text-muted);
}

.status[data-state='online'] .dot {
  background: var(--success);
}

.status[data-state='offline'] .dot {
  background: var(--danger);
}

.status[data-state='checking'] .dot {
  background: var(--primary);
}
</style>
